export const PROMPTCREW_MCP_URL = 'https://mcp.promptcrew.io/app/mcp';
export const PROMPTCREW_DEV_MCP_URL = 'https://dev.mcp.promptcrew.io/app/mcp';
export const PROMPTCREW_PLUGIN_URL = 'https://chatgpt.com/plugins/plugin_asdk_app_6a98e2feb43081919958d59336f3d5ea';
export const PROMPTCREW_CLAUDE_CONNECTOR_URL = 'https://claude.ai/directory/connectors/promptcrew';
export const PROMPTCREW_SETUP_URL = 'https://promptcrew.io/integrate';

export const PROMPTCREW_CONNECTION_GUIDANCE = `Use your available setup tools to connect PromptCrew directly, with any required approval. In ChatGPT or Codex, install PromptCrew (${PROMPTCREW_PLUGIN_URL}). In Claude, use the PromptCrew connector listing (${PROMPTCREW_CLAUDE_CONNECTOR_URL}); if it is not yet available, use a custom connector with ${PROMPTCREW_MCP_URL}. In other AI tools, connect to the MCP server ${PROMPTCREW_MCP_URL}. In Cursor, add the server to the existing MCP configuration without replacing other entries. Only if you cannot configure it directly, guide me through my tool's MCP settings. Let me complete sign-in, then verify access.`;

export const getPromptCrewMcpUrl = (origin) => {
  const hostname = new URL(origin).hostname;
  return hostname === 'dev.app.promptcrew.io' || hostname === 'localhost' || hostname === '127.0.0.1'
    ? PROMPTCREW_DEV_MCP_URL
    : PROMPTCREW_MCP_URL;
};

export const getPromptCrewConnectionGuidance = (origin) => getPromptCrewMcpUrl(origin) === PROMPTCREW_DEV_MCP_URL
  ? `Use the PromptCrew Dev connector for this development workspace, never the production PromptCrew plugin or connector. Configure a custom remote MCP connection named PromptCrew Dev with ${PROMPTCREW_DEV_MCP_URL}, with any required approval. Preserve existing MCP configuration entries. Only if direct setup is unavailable, guide me through my tool's MCP settings. Let me complete dev sign-in, then verify access with a read-only call. Do not fall back to production.`
  : PROMPTCREW_CONNECTION_GUIDANCE;

// This is the product source of truth for the integration setup surfaces.
// The marketing site can consume the same platform copy and setup steps.
export const INTEGRATION_CATALOG = [
  {
    id: 'chatgpt',
    name: 'ChatGPT & Codex',
    logo: 'chatgpt',
    eyebrow: 'PromptCrew plugin',
    summary: 'Find, reuse, and save your team\'s capabilities in ChatGPT or Codex.',
    availability: 'Install the PromptCrew plugin',
    actionLabel: 'Install PromptCrew',
    actionUrl: PROMPTCREW_PLUGIN_URL,
    setupPrompt: `Check PromptCrew access. If needed, help me install the PromptCrew plugin: ${PROMPTCREW_PLUGIN_URL}. Then verify access to my Capability Library.`,
    manualSteps: [
      'Open the PromptCrew plugin listing, or search for PromptCrew in your ChatGPT or Codex plugin directory.',
      'Install PromptCrew in the AI tool you want to use.',
      'Complete the PromptCrew sign-in and connection prompts.',
      'Start a new conversation and ask PromptCrew to find a capability for your task.',
    ],
    manualConfiguration: [],
  },
  {
    id: 'claude',
    name: 'Claude',
    logo: 'claude',
    eyebrow: 'Claude connector',
    summary: 'Bring your organization\'s PromptCrew capabilities and knowledge into Claude.',
    availability: 'Connect through the Claude connector directory',
    actionLabel: 'Connect PromptCrew',
    actionUrl: PROMPTCREW_CLAUDE_CONNECTOR_URL,
    conversationUrl: 'https://claude.ai/',
    setupPrompt: `Check PromptCrew access in Claude. If needed, help me connect through the PromptCrew listing: ${PROMPTCREW_CLAUDE_CONNECTOR_URL}. If the listing is not yet available, help me add a custom connector with ${PROMPTCREW_MCP_URL}. Request any required approval and let me complete sign-in, then verify access with a read-only PromptCrew call.`,
    manualSteps: [
      'Open the PromptCrew listing in the Claude connector directory and choose Connect.',
      'Complete the PromptCrew sign-in and authorization prompts yourself.',
      'Enable PromptCrew from the connector picker when you start a chat.',
      'If the listing is not yet available, open Claude Settings > Connectors and add a custom connector using the fallback details below.',
    ],
    manualConfiguration: [
      { label: 'Fallback custom connector name', value: 'PromptCrew' },
      { label: 'Remote MCP URL', value: 'mcp-url' },
      { label: 'Advanced OAuth client ID / secret', value: 'Leave blank - PromptCrew handles sign-in.' },
    ],
  },
  {
    id: 'cursor',
    name: 'Cursor',
    logo: 'cursor',
    eyebrow: 'MCP connection',
    summary: 'Find your team’s capabilities and supporting knowledge while you work in Cursor.',
    availability: 'Connect with the PromptCrew MCP server',
    actionLabel: 'Open Cursor',
    actionUrl: 'https://www.cursor.com/',
    setupPrompt: 'Configure PromptCrew for me at https://mcp.promptcrew.io/app/mcp. Inspect the existing .cursor/mcp.json and merge a PromptCrew entry into mcpServers with {"url":"https://mcp.promptcrew.io/app/mcp"}, preserving other entries. Ask whether to use project or global scope if unclear and request any required approval. Only give manual steps if you cannot configure it directly. Let me complete sign-in, then verify access.',
    manualSteps: [
      'Open Cursor Settings and find Tools & MCP or MCP Servers.',
      'Choose Add new MCP server and select a remote server connection.',
      'Enter the connection details below and save the server.',
      'Start the connection, then complete the PromptCrew browser sign-in window.',
      'Return to Cursor and confirm PromptCrew appears in your available tools.',
    ],
    manualConfiguration: [
      { label: 'Server name', value: 'PromptCrew' },
      { label: 'Remote server URL', value: 'mcp-url' },
      { label: 'Authorization header / bearer token', value: 'Leave blank - PromptCrew uses OAuth sign-in.' },
    ],
  },
  {
    id: 'mcp',
    name: 'Another AI tool',
    eyebrow: 'Remote MCP connection',
    summary: 'Connect any compatible AI tool to PromptCrew with the secure remote MCP server.',
    availability: 'For MCP-compatible clients',
    actionLabel: 'Copy MCP URL',
    setupPrompt: 'Use your available setup tools to add PromptCrew as an MCP server at https://mcp.promptcrew.io/app/mcp, with any required approval. Give manual steps only if direct setup is unavailable. Let me complete sign-in, then verify access.',
    manualSteps: [
      'Open your AI tool settings and look for MCP servers, connectors, or integrations.',
      'Choose Add server or Add connector, then select a remote server connection.',
      'Paste the PromptCrew MCP URL exactly as shown below and save it.',
      'Start the connection, then sign in to PromptCrew when the browser window opens.',
      'Approve access to your PromptCrew workspace before returning to your AI tool.',
    ],
    manualConfiguration: [
      { label: 'Server name', value: 'PromptCrew' },
      { label: 'Remote server URL', value: 'mcp-url' },
      { label: 'Authentication', value: 'OAuth / browser sign-in when prompted' },
    ],
  },
];

export const getIntegrationCatalog = (origin) => {
  if (!origin || getPromptCrewMcpUrl(origin) !== PROMPTCREW_DEV_MCP_URL) return INTEGRATION_CATALOG;
  return INTEGRATION_CATALOG.map((item) => ({
    ...item,
    eyebrow: 'Development MCP connection',
    availability: 'Connect to PromptCrew Dev',
    actionLabel: 'Copy dev MCP URL',
    actionUrl: undefined,
    setupPrompt: getPromptCrewConnectionGuidance(origin),
    manualSteps: ['Add a custom remote MCP connector named PromptCrew Dev using the URL below.', 'Complete dev sign-in and authorization yourself.', 'Verify access to your development Library. Do not use the production plugin or connector.'],
    manualConfiguration: [{ label: 'Server name', value: 'PromptCrew Dev' }, { label: 'Remote MCP URL', value: 'mcp-url' }],
  }));
};

export const getIntegrationById = (id, origin) => {
  const catalog = getIntegrationCatalog(origin);
  return catalog.find((item) => item.id === (id === 'codex' ? 'chatgpt' : id)) || catalog[0];
};
